import React from 'react';
import { isSafari, isIOS } from 'react-device-detect';
import { Box } from '@mui/material';
import { makeStyles } from '@mui/styles';
import PortraitPaperLayout from '../@common/portrait/PortraitPaperLayout';
import PageLines from '../@common/PageLines';
import { SmallPageLineText } from '../@common/PageLineText';
import BaseInput from '../@common/BaseInput';
import GroupedPageLines from '../@common/GroupedPageLines';
import NameAndDateLines from '../@common/NameAndDateLines';
import PortraitPageLines from '../@common/portrait/PortraitPageLines';
import { determineStyleBasedOnLineWidth, getFontSize } from '../../utils/style-utils';
import MyOpinionTitle from './title';
import ReasonRow from './reasonRow';
import { translate } from '../../translations/translator';

const t = key => translate('worksheet', key);

const portraitLines = [...Array(2).keys()];

const MyOpinion = ({ onChange, isCursive, isNarrow, valueMap, printRef }) => {
  const bookFont = isCursive ? 'Cursive-Book' : 'Print-Wide-Book';
  const fontSize = getFontSize(isCursive, isNarrow);
  const marginTop = determineStyleBasedOnLineWidth(isCursive, isNarrow, {
    cursiveAndNarrow: isIOS || isSafari ? '-20px' : '-26px',
    narrow: '-15px',
    cursive: isIOS || isSafari ? '-27px' : '-36px',
    regular: '-24px'
  });
  const marginTopInput = determineStyleBasedOnLineWidth(isCursive, isNarrow, {
    cursiveAndNarrow: isSafari || isIOS ? '-14px' : '-24px',
    narrow: isIOS ? '-20.5px' : '-21px',
    cursive: isSafari || isIOS ? '-20px' : '-36px',
    regular: '-33px'
  });
  const reasonsMarginTop = isNarrow ? '40px' : '50px';
  const contentMarginTop = isNarrow ? '-45px' : '-25px';
  const imageMarginTop = isNarrow ? '50px' : '0';

  const classes = useStyles({ marginTopInput, isSafari });

  return (
    <PortraitPaperLayout
      isNarrow={isNarrow}
      printRef={printRef}
      className={isSafari ? 'print-scale-down' : ''}
    >
      <Box style={{ marginTop: imageMarginTop }}>
        <MyOpinionTitle displayClouds={!isNarrow} />
      </Box>
      <Box
        style={{
          marginTop: contentMarginTop
        }}
      >
        <NameAndDateLines
          isCursive={isCursive}
          fontFamily={bookFont}
          top={0}
          left={10}
          isNarrow={isNarrow}
          width={775}
        />
      </Box>
      <PageLines width={775} className={classes.pageLine} isNarrow={isNarrow}>
        <BaseInput
          onChange={({ target }) => {
            onChange({
              // eslint-disable-next-line
              [1]: target.value
            });
          }}
          showBackground={true}
          fontFamily={bookFont}
          color="#000"
          placeholder={t('enterTopic')}
          className={classes.input}
          fontSize={fontSize}
          maxLength={45}
          value={valueMap[1]}
        />
      </PageLines>
      <Box
        style={{
          display: 'flex',
          flexDirection: 'column',
          marginTop: isNarrow ? '-18px' : '-12px'
        }}
      >
        <>
          <GroupedPageLines
            top={8}
            width={775}
            fontSize={fontSize}
            color="black"
            marginLeftText={3}
            marginTopText={marginTop}
            marginPageLines="10px 25px 0px"
            fontFamily={bookFont}
            isNarrow={isNarrow}
          >
            {t('opinion')}:
          </GroupedPageLines>
          <GroupedPageLines
            top={15}
            width={775}
            marginPageLines="40px 25px 0px"
            isNarrow={isNarrow}
          />
          {isNarrow && (
            <GroupedPageLines top={15} width={775} marginPageLines="40px 25px 0px" isNarrow />
          )}
        </>
        <Box style={{ marginTop: reasonsMarginTop, marginLeft: 15 }}>
          <ReasonRow
            fontSize={fontSize}
            fontFamily={bookFont}
            marginTop={marginTop}
            isNarrow={isNarrow}
          />
          <GroupedPageLines
            width={735}
            marginPageLines={isNarrow ? '-10px 20px 40px 60px' : '15px 20px 40px 60px'}
            isNarrow={isNarrow}
          />
          {portraitLines.map(x => {
            return (
              <PortraitPageLines
                singleColumn
                width={735}
                rowMargin={isNarrow ? '-10px 0 40px 40px' : '0 0 40px 40px'}
                isNarrow={isNarrow}
                rowPadding="15px 20px 5px 20px"
                marginTop={45}
                col1={
                  <>
                    <SmallPageLineText isNarrow={isNarrow}>
                      <div className="pageNumber">{x + 2}.</div>
                    </SmallPageLineText>
                  </>
                }
              />
            );
          })}
        </Box>

        <GroupedPageLines
          top={isNarrow ? -45 : -35}
          width={775}
          fontSize={fontSize}
          color="black"
          marginLeftText={3}
          marginTopText={marginTop}
          marginPageLines="40px 25px 0px"
          fontFamily={bookFont}
          isNarrow={isNarrow}
        >
          {t('conclusion')}:
        </GroupedPageLines>
        <GroupedPageLines
          top={isNarrow ? -80 : -30}
          width={775}
          marginPageLines={isNarrow ? '80px 25px 0px' : '40px 25px 0px'}
          isNarrow={isNarrow}
        />
        {isNarrow && (
          <GroupedPageLines top={-75} width={775} marginPageLines="40px 25px 0px" isNarrow />
        )}
      </Box>
    </PortraitPaperLayout>
  );
};

const useStyles = makeStyles(() => ({
  pageLine: {
    alignSelf: 'center !important',
    margin: '100px 25px 0px !important',
    position: 'relative',
    top: '-50px !important',
    left: '0px !important'
  },
  pageLineText: {
    justifyContent: 'space-between !important',
    width: '514px !important',
    marginLeft: '20px !important',
    fontSize: '30px !important',
    marginTop: '-26px !important'
  },
  input: ({ marginTopInput }: any) => ({
    width: '775px !important',
    marginLeft: '0px !important',
    paddingLeft: '10px',
    marginTop: `${marginTopInput} !important`,
    color: 'black'
  })
}));
export default MyOpinion;
